import axios from 'axios';

export interface PartnerShopDetailShop {
  id: number;
  name: string;
  category: string[];
  description: string;
  address?: string | null;
  location: string;
  lat?: number | string | null;
  lon?: number | string | null;
  lng?: number | string | null;
  naverPlaceId?: number | string | null;
  reviewCount: number;
  photoCount: number;
  verified: boolean;
}

export interface PartnerShopDetailHeroImages {
  mobile: string;
  desktop: string;
  gallery: string[];
}


export interface PartnerShopDetailTextItem {
  title: string;
  desc: string;
}

export interface PartnerSummarySection {
  introText: string;
  items: PartnerShopDetailTextItem[]
}

export interface PartnerShopDetailInsightItem {
  title: string;
  description: string;
}

export interface PartnerShopDetailBrandStory {
  description: string;
  points: PartnerShopDetailTextItem[];
}

export interface PartnerShopDetailReviewPhoto {
  id: number | string;
  src: string;
  label: string;
  category: string;
}

export interface PartnerShopDetailReviewSummary {
  totalReviewCount: number;
  quote: string;
  pros: PartnerShopDetailInsightItem[];
  notes: PartnerShopDetailInsightItem[];
}

export interface PartnerShopDetailReview {
  id: number | string;
  title: string;
  date: string;
  source: string;
  writer?: {
    id: number | string;
    nickname: string;
    profileImageUrl?: string;
    memberLevel?: number;
    memberLevelName?: string;
    memberLevelIconUrl?: string;
    isStaff?: boolean;
    isManager?: boolean;
  };
  summary: string;
  photoThumbnails: string[];
  reviewText?: string;
  contentPreview: string;
  tags: string[];
  goodPoints: string[];
  checkPoints: string[];
  originalUrl: string;
  evaluationLevel?: string | null;
  sentiment?: string | null;
  confidence?: number | null;
}

export interface PartnerShopDetailProduct {
  title: string;
  desc: string;
  price: string;
}

export interface PartnerShopDetailFaq {
  question: string;
  answer: string;
}

export interface PartnerShopDetailInfoItem {
  label: string;
  value: string;
}

export interface PartnerShopDetailBenefit {
  title: string;
  condition: string;
  badge?: string;
}

export interface PartnerShopDetailVendorBenefits {
  vendorName: string;
  vendorCategory: string;
  benefits: PartnerShopDetailBenefit[];
}

export interface PartnerShopDetailLookbookPhoto {
  id: string;
  lookbookSectionId: number;
  category: string;
  categoryLabel: string;
  title: string;
  description: string;
  src: string;
  thumbnailSrc?: string;
  isRepresentative?: number;
  sortOrder?: number;
}

export interface PartnerShopDetailLookbookSection {
  id: number;
  sectionName: string;
  description: string;
  sortOrder: number;
  photos: PartnerShopDetailLookbookPhoto[];
}

export interface PartnerShopPlannerContentPhotoRef {
  photoKey: string;
  caption?: string;
  alt?: string;
}

export interface PartnerShopPlannerPhoto {
  partnerShopReviewPhotoId?: number | string;
  imageUrl: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  photoCategory?: string;
  imageCategoryName?: string;
  imageSummary?: string;
  usableForApp?: boolean;
  isRepresentative?: boolean;
  photoKey: string;
  caption?: string;
  alt?: string;
}

export type PartnerShopPlannerContentPhoto = PartnerShopPlannerPhoto | PartnerShopPlannerContentPhotoRef;

export interface PartnerShopPlannerContentSection {
  title: string;
  body: string;
  factIds?: string[];
  photos?: PartnerShopPlannerPhoto[];
  photoKeys?: string[];
}

export interface PartnerShopPlannerContent {
  partnerShopId: number;
  shopName: string;
  eyebrow: string;
  headline: string;
  introduction: string;
  metaItems?: string[];
  heroPhotos?: PartnerShopPlannerContentPhoto[];
  sections?: PartnerShopPlannerContentSection[];
  plannerQuote?: {
    text: string;
    factIds?: string[];
  };
  recommendation?: {
    title: string;
    body: string;
    factIds?: string[];
  };
  visitChecks?: Array<{
    title: string;
    description: string;
    factIds?: string[];
  }>;
}

export interface PartnerShopDetailPageData {
  shop: PartnerShopDetailShop;
  address?: string;
  heroImages: PartnerShopDetailHeroImages;
  brandStory: PartnerShopDetailBrandStory;
  verifiedReasonsJson: PartnerSummarySection;
  promotionBenefits: PartnerShopDetailVendorBenefits[];
  reviewPhotos: PartnerShopDetailReviewPhoto[];
  reviewSummary: PartnerShopDetailReviewSummary;
  reviews?: PartnerShopDetailReview[];
  brandLookbook: string[];
  brandLookbookSections?: PartnerShopDetailLookbookSection[];
  products: PartnerShopDetailProduct[];
  faqs: PartnerShopDetailFaq[];
  information: PartnerShopDetailInfoItem[];
  plannerContent?: PartnerShopPlannerContent | null;
  plannerPhotos?: PartnerShopPlannerPhoto[];
  plannerContentJson?: PartnerShopPlannerContent | null;
  plannerPhotosJson?: PartnerShopPlannerPhoto[];
}

export async function getPartnerShopDetail(shopSlug: string, options?: { preview?: boolean }) {
  const response = await axios.get(`/api/partner-shops/${shopSlug}`, {
    params: {
      preview: options?.preview ? 1 : undefined,
    },
  });

  const result = response.data;

  if (!result || result.status !== 200) {
    throw new Error(result?.message || '업체 상세 정보를 불러오지 못했습니다.');
  }

  return result.data as PartnerShopDetailPageData;
}



export interface PartnerShopReviewInsight {
  title: string;
  description: string;
}

export interface PartnerShopReviewItem {
  id: number;
  title: string;
  date: string;
  source: string;
  writer?: {
    id: number | string;
    nickname: string;
    profileImageUrl?: string;
    memberLevel?: number;
    memberLevelName?: string;
    memberLevelIconUrl?: string;
    isStaff?: boolean;
    isManager?: boolean;
  };
  summary: string;
  photoThumbnails: string[];
  reviewText?: string;
  contentPreview: string;
  tags: string[];
  goodPoints: string[];
  checkPoints: string[];
  originalUrl: string;
  evaluationLevel?: string | null;
  sentiment?: string | null;
  confidence?: number | null;
}

export interface PartnerShopReviewPageData {
  shop: {
    id: number;
    name: string;
    slug: string;
    category: string[];
    description: string;
    location: string;
    verified: boolean;
  };

  overview: {
    total: number;
    evaluated: number;
    direct: number;
    indirect: number;
    mentionedOnly: number;
  };

  summary: {
    totalReviewCount: number;
    quote: string;
    pros: PartnerShopReviewInsight[];
    notes: PartnerShopReviewInsight[];
  };

  filters: Array<{
    label: string;
    count: number;
  }>;
  positiveBars: Array<{ label: string; count: number }>;
  cautionBars: Array<{ label: string; count: number }>;
  checkTips: string[];

  reviews: PartnerShopReviewItem[];
}

export async function getPartnerShopReviews(
  shopSlug: string,
  options?: {
    preview?: boolean;
  }
) {
  const response = await axios.get(`/api/partner-shops/${shopSlug}/reviews`, {
    params: {
      preview: options?.preview ? 1 : undefined,
    },
  });

  const result = response.data;

  if (!result || result.status !== 200) {
    throw new Error(result?.message || '후기 정보를 불러오지 못했습니다.');
  }

  return result.data as PartnerShopReviewPageData;
}





export interface PartnerShopLookbookPartnerShop {
  id: number;
  shopName: string;
  displayName: string;
  shopSlug: string;
  shopCategory: string;
  shortDescription?: string;
}

export interface LookbookCategory {
  id: string;
  label: string;
  description?: string;
  count: number;
  sortOrder?: number;
}

export interface LookbookPhoto {
  id: string;
  category: string;
  categoryLabel: string;
  title: string;
  description: string;
  src: string;
  thumbnailSrc?: string;
  isRepresentative?: number;
  sortOrder?: number;
}

export interface PartnerShopLookbookData {
  partnerShop: PartnerShopLookbookPartnerShop;
  categories: LookbookCategory[];
  photos: LookbookPhoto[];
}

export async function getPartnerShopLookbookBySlug(
  shopSlug: string
): Promise<PartnerShopLookbookData> {
  return axios.get(
    `/api/partner-shops/${encodeURIComponent(shopSlug)}/lookbook`
  ).then(res => res.data);

  
}

export interface MakeConsultationPayload {
  name: string;
  shopSlug: string;
  visitDate: string;
  etc: string;
  phone: string;
}

export interface MakeConsultationResponse {
  status: number;
  message?: string;
  data?: unknown;
}

export async function makeConsultation ({
  name,
  shopSlug,
  visitDate,
  etc,
  phone
}: MakeConsultationPayload): Promise<MakeConsultationResponse> {
  return axios.post(
    `/api/partner-shops/${encodeURIComponent(shopSlug)}/consultation`, {
      visitDate,
      name,
      etc,
      phone
    }
  ).then(res => res.data);
}
